import React, { useState, useRef, useContext, useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useHistory } from "react-router-dom";
import axios from 'axios';
import { isAuthAtom, dataUserAtom, setDataUserAtom } from '../../Services/store/atoms';
import config from '../../Config';
import DarkModeContext from '../../Components/DarkModeContext';
import Modal from '../../Components/Modal';
import ModalActions from '../../Components/ModalActions';
import LayoutHomeUser from '../../Layouts/Model/Layout';
import { FaUser, FaWhatsapp, FaSpinner, FaChevronLeft, FaCamera, FaImage } from 'react-icons/fa';
import { LuVenetianMask } from 'react-icons/lu';
import { FaVenusMars } from 'react-icons/fa6';
import Paises from '../../Components/paises';
import DateOfBirthSelector from '../../Components/DateOfBirthSelector';
import { getCountryCallingCode } from 'libphonenumber-js';
import useNicknameAvailability from '../../hooks/useNicknameAvailability';

const TOTAL_SLIDES = 6;
const slideLabels = ['Nickname', 'Tus datos', 'Sobre vos', 'Ubicación', 'Foto de perfil', 'Foto de portada'];

const getUserCountry = async () => {
    try {
        const response = await axios.get('https://api.ipgeolocation.io/ipgeo?apiKey=4dd5108036e74396ac71b760775b0f39');
        return response.data.country_code2;
    } catch (error) {
        console.error('Error detecting user country:', error);
        return null;
    }
};

const getPhoneCode = (countryCode) => {
    return getCountryCallingCode(countryCode);
};

// Selector de una sola foto con preview, usado para perfil y portada.
function PhotoPicker({ file, onPick, isLoading, icon: Icon, label, darkMode }) {
    const inputRef = useRef(null);
    const previewUrl = file ? URL.createObjectURL(file) : null;

    return (
        <div
            onClick={() => !isLoading && inputRef.current?.click()}
            className={`relative w-full rounded-2xl border-2 border-dashed cursor-pointer flex flex-col items-center justify-center overflow-hidden ${
                darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-gray-50'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ aspectRatio: '4 / 3' }}
        >
            {previewUrl ? (
                <img src={previewUrl} alt={label} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
                <>
                    <Icon className={`text-3xl mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{label}</p>
                </>
            )}
            {previewUrl && (
                <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-xs text-center py-1.5">
                    Toca para cambiar
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                disabled={isLoading}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onPick(f);
                }}
            />
        </div>
    );
}

function ModelCompleteRegistration() {
    const history = useHistory();
    const [isAuth] = useAtom(isAuthAtom);
    const [dataUser] = useAtom(dataUserAtom);
    const setDataUser = useSetAtom(setDataUserAtom);
    const { darkMode } = useContext(DarkModeContext);
    const modalRef = useRef(null);

    const [slide, setSlide] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [name, setName] = useState('');
    const [nameV, setNameV] = useState(false);
    const [nickname, setNickname] = useState('');
    const [nicknameV, setNicknameV] = useState(false);
    const [phone, setPhone] = useState('');
    const [phoneV, setPhoneV] = useState(false);
    const [selectedAreaCode, setSelectedAreaCode] = useState('');
    const [phoneCode, setPhoneCode] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [gender, setGender] = useState('');
    const [genderV, setGenderV] = useState(false);
    const [zipCode, setZipCode] = useState('');
    const [Birthdate, setBirthdate] = useState('');
    const [BirthdateV, setBirthdateV] = useState(false);
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [coverPhoto, setCoverPhoto] = useState(null);
    const [hasShownPhoneWarning, setHasShownPhoneWarning] = useState(false);
    const nicknameStatus = useNicknameAvailability(nickname);

    useEffect(() => {
        if (!isAuth || dataUser.role !== 'model') {
            history.push('/');
            return;
        }
        // Se saltea el onboarding si ya lo completó una vez, o si ya tenía
        // todos los datos básicos cargados de antes (perfiles viejos, previos
        // a este flujo, que nunca van a tener model_onboarding_completed_at).
        // Si le falta algo pero llegó por URL directa quedándose a mitad de
        // camino, entra igual para terminarlo.
        // La API ya no envía birthdate (privacidad); age == null significa
        // que todavía no cargó su fecha real.
        const hasBasicData = dataUser.name && dataUser.nickname && dataUser.phone
            && dataUser.age != null;
        if (dataUser.model_onboarding_completed_at || hasBasicData) {
            history.push('/model');
            return;
        }
        if (dataUser.name) setName(dataUser.name);
        if (dataUser.nickname) setNickname(dataUser.nickname);
        if (dataUser.phone) setPhone(dataUser.phone);
    }, [isAuth, dataUser, history]);

    useEffect(() => {
        const fetchCountryCode = async () => {
            const countryCode = await getUserCountry();
            if (countryCode) {
                const phoneCode = getPhoneCode(countryCode);
                setPhoneCode(phoneCode);
                setSelectedAreaCode(`+${phoneCode}`);
                setSelectedCountry({ value: countryCode, label: countryCode, flag: countryCode });
            }
        };
        fetchCountryCode();
    }, []);

    const handleCountryChange = (selectedOption) => {
        setSelectedCountry(selectedOption);
        setZipCode(selectedOption.postal);
    };

    const handlePhoneChange = (event) => {
        const inputValue = event.target.value;
        setPhone(inputValue);
        if (!selectedAreaCode || selectedAreaCode === '+') {
            setSelectedAreaCode(`+${phoneCode}`);
        } else if (!selectedAreaCode.startsWith('+')) {
            setSelectedAreaCode(`+${selectedAreaCode}`);
        }
    };

    const done = [
        nickname.length >= 3 && nicknameStatus === 'available',
        !!name && !!phone,
        !!gender && !!Birthdate,
        !!selectedCountry,
        !!profilePhoto,
        !!coverPhoto,
    ];

    const goBack = () => setSlide((s) => Math.max(0, s - 1));
    const goNext = () => setSlide((s) => Math.min(TOTAL_SLIDES - 1, s + 1));

    const showError = (title, body) => {
        modalRef.current.openModal(
            'error', title,
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{body}</p>,
            <ModalActions onClose={() => modalRef.current.closeModal()} />
        );
    };

    const submitAll = async () => {
        if (!done[5]) return;
        setIsLoading(true);
        try {
            const profileData = new FormData();
            profileData.append('name', name);
            profileData.append('nickname', nickname);
            profileData.append('phone', `${selectedAreaCode}${phone}`);
            profileData.append('gender', gender);
            profileData.append('Birthdate', Birthdate);
            profileData.append('nationality', selectedCountry ? selectedCountry.value : '');
            profileData.append('gps', zipCode);
            await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/auth/complete-profile`, profileData, {
                headers: { 'content-type': 'multipart/form-data' },
            });

            const profilePhotoData = new FormData();
            profilePhotoData.append('photo', profilePhoto);
            await axios.post(`${config.REACT_APP_BASE_URL}/api/dasboard/v1/model/profile-photo`, profilePhotoData, {
                headers: { 'content-type': 'multipart/form-data' },
            });

            const coverPhotoData = new FormData();
            coverPhotoData.append('photo', coverPhoto);
            await axios.post(`${config.REACT_APP_BASE_URL}/api/dasboard/v1/model/background`, coverPhotoData, {
                headers: { 'content-type': 'multipart/form-data' },
            });

            await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/auth/complete-onboarding`);

            // Sin esto, dataUser sigue teniendo la foto de antes de completar el
            // onboarding: al navegar a /model, RouteCheck la vuelve a mandar para
            // acá (sigue viendo nickname/phone/birthdate vacíos) y queda en loop
            // hasta que hace un refresh manual que sí trae los datos frescos.
            const freshData = await axios.get(`${config.REACT_APP_BASE_URL}/api/v1/auth/data`);
            setDataUser(freshData.data);

            setIsLoading(false);
            modalRef.current.openModal(
                'success', '¡Tu perfil está listo!',
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    Tus fotos quedaron enviadas a verificación. Cuando quieras, podés subir tu documento de identidad
                    desde tu panel para que aprueben tu cuenta.
                </p>,
                <ModalActions onClose={() => {
                    modalRef.current.closeModal();
                    history.push('/model');
                }} />
            );
        } catch (error) {
            setIsLoading(false);
            const errMsg = error.response?.data?.message || 'Ocurrió un error al enviar tu perfil';
            const details = error.response?.data?.errors
                ? Object.values(error.response.data.errors).flat().join(', ')
                : '';
            showError(errMsg, details || 'Probá de nuevo.');
        }
    };

    const validate = (field) => {
        if (field) return "animate__animated animate__flash border-2 border-rose-500";
    };

    const continueBtn = (enabled, onClick, label = 'Continuar →') => (
        <button
            onClick={onClick || goNext}
            disabled={!enabled || isLoading}
            className={`w-full max-w-sm mx-auto block py-3.5 rounded-2xl font-semibold text-sm transition-all duration-200 ${
                enabled && !isLoading
                    ? 'bg-rose-500 text-white hover:bg-rose-600 active:scale-95 shadow-sm shadow-rose-200'
                    : (darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
            }`}
        >
            {isLoading ? <FaSpinner className="animate-spin mx-auto" /> : label}
        </button>
    );

    return (
        <LayoutHomeUser>
            <h1 className={`text-2xl font-semibold text-center mb-6 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Completa tu perfil ❤️
            </h1>
            <div className="flex justify-center pt-6">
                    <div className={`w-full rounded-xl md:mt-1 sm:max-w-md ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm overflow-hidden`}>

                        {/* ── Barra de progreso ── */}
                        <div className={`flex items-center gap-2 px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                            <div className="w-7 flex-shrink-0">
                                {slide > 0 && (
                                    <button
                                        onClick={goBack}
                                        disabled={isLoading}
                                        className={`p-1.5 rounded-full transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                        aria-label="Volver al paso anterior"
                                    >
                                        <FaChevronLeft className="text-sm" />
                                    </button>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-baseline gap-2 text-xs font-semibold mb-1">
                                    <span className={`flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Paso {slide + 1} de {TOTAL_SLIDES}
                                    </span>
                                    <span className={`truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{slideLabels[slide]}</span>
                                </div>
                                <div className={`h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                    <div
                                        className="h-full bg-rose-500 rounded-full transition-all duration-500"
                                        style={{ width: `${((slide + 1) / TOTAL_SLIDES) * 100}%` }}
                                    />
                                </div>
                            </div>
                            <div className="w-7 flex-shrink-0" />
                        </div>

                        {/* ── Slides ── */}
                        <div className="overflow-hidden">
                            <div
                                className="flex items-start"
                                style={{
                                    width: `${TOTAL_SLIDES * 100}%`,
                                    transform: `translateX(-${slide * (100 / TOTAL_SLIDES)}%)`,
                                    transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
                                }}
                            >
                                {/* Slide 0: Nickname */}
                                <div className="p-6 space-y-4" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
                                    <h1 className={`text-xl font-semibold text-center ${darkMode ? 'text-rose-500' : 'text-rose-500'}`}>Nickname</h1>
                                    <p className={`text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Así te van a encontrar los clientes. Solo minúsculas y números, sin espacios.
                                    </p>
                                    <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-2.5 rounded-[10px] shadow flex flex-row items-center ${validate(nicknameV)}`}>
                                        <LuVenetianMask className={`flex-shrink-0 ${darkMode ? 'text-white' : 'text-gray-500'}`} />
                                        <input
                                            type="text"
                                            value={nickname}
                                            onChange={(e) => {
                                                const v = e.target.value.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
                                                setNickname(v);
                                                setNicknameV(false);
                                            }}
                                            placeholder="tunickname"
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            spellCheck={false}
                                            autoComplete="off"
                                            className={`ml-3 w-0 text-base ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'} border-transparent focus:border-transparent focus:ring-0 flex-grow flex-shrink`}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    {nickname && nicknameStatus === 'checking' && (
                                        <div className="text-gray-400 text-sm ml-1">Comprobando disponibilidad...</div>
                                    )}
                                    {nickname && nicknameStatus === 'available' && (
                                        <div className="text-green-500 text-sm ml-1">Nickname disponible</div>
                                    )}
                                    {nickname && nicknameStatus === 'taken' && (
                                        <div className="text-red-500 text-sm ml-1">Ese nickname ya está en uso</div>
                                    )}
                                    {nickname && nickname.length < 3 && (
                                        <div className="text-gray-400 text-sm ml-1">Mínimo 3 caracteres</div>
                                    )}
                                    <div className="pt-2">{continueBtn(done[0])}</div>
                                </div>

                                {/* Slide 1: Nombre + Teléfono */}
                                <div className="p-6 space-y-4" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
                                    <h1 className={`text-xl font-semibold text-center ${darkMode ? 'text-rose-500' : 'text-rose-500'}`}>Tus datos</h1>
                                    <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-2.5 rounded-[10px] shadow flex flex-row items-center ${validate(nameV)}`}>
                                        <FaUser className={`flex-shrink-0 ${darkMode ? 'text-white' : 'text-gray-500'}`} />
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => { setName(e.target.value); setNameV(false); }}
                                            placeholder="Nombre"
                                            autoComplete="name"
                                            className={`ml-3 w-0 text-base ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'} border-transparent focus:border-transparent focus:ring-0 flex-grow flex-shrink`}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-2.5 rounded-[10px] shadow flex flex-row items-center ${validate(phoneV)}`}>
                                        <FaWhatsapp className={`flex-shrink-0 ${darkMode ? 'text-white' : 'text-gray-500'}`} />
                                        <input
                                            type="text"
                                            value={selectedAreaCode}
                                            onChange={(e) => setSelectedAreaCode(e.target.value)}
                                            placeholder="Área"
                                            className={`ml-3 text-base flex-shrink-0 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'} border-transparent focus:border-transparent focus:ring-0`}
                                            disabled={isLoading}
                                            style={{ width: '3.5rem' }}
                                            inputMode="numeric"
                                            onKeyDown={(e) => { if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') e.preventDefault(); }}
                                        />
                                        <input
                                            type="text"
                                            value={phone}
                                            onChange={handlePhoneChange}
                                            onFocus={() => {
                                                if (!hasShownPhoneWarning) {
                                                    modalRef.current.openModal(
                                                        '',
                                                        <div className={`p-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}>
                                                            <p className="text-sm">
                                                                El número tiene que ser un WhatsApp activo: ahí llegan las notificaciones del sistema y el soporte.
                                                            </p>
                                                        </div>,
                                                        <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`}>
                                                            <button type="button"
                                                                className="w-full inline-flex justify-center rounded-full bg-rose-500 text-white px-4 py-2 text-base font-medium hover:bg-rose-600 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                                                                onClick={() => { modalRef.current.closeModal(); setHasShownPhoneWarning(true); }}
                                                            >Cerrar</button>
                                                        </div>
                                                    );
                                                }
                                            }}
                                            placeholder="Teléfono"
                                            autoComplete="tel-national"
                                            className={`ml-2 w-0 text-base ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'} border-transparent focus:border-transparent focus:ring-0 flex-grow flex-shrink`}
                                            disabled={isLoading}
                                            inputMode="numeric"
                                            onKeyDown={(e) => { if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') e.preventDefault(); }}
                                        />
                                    </div>
                                    <div className="pt-2">{continueBtn(done[1])}</div>
                                </div>

                                {/* Slide 2: Género + Fecha de nacimiento */}
                                <div className="p-6 space-y-4" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
                                    <h1 className={`text-xl font-semibold text-center ${darkMode ? 'text-rose-500' : 'text-rose-500'}`}>Sobre vos</h1>
                                    <div className={`${darkMode ? 'bg-gray-700' : 'bg-white'} p-2.5 rounded-[10px] shadow flex flex-row items-center ${validate(genderV)}`}>
                                        <FaVenusMars className={`flex-shrink-0 ${darkMode ? 'text-white' : 'text-gray-500'}`} />
                                        <select
                                            value={gender}
                                            onChange={(e) => { setGender(e.target.value); setGenderV(false); }}
                                            className={`ml-3 w-0 text-base py-1 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-black'} border-transparent focus:border-transparent focus:ring-0 flex-grow flex-shrink ${!gender ? 'text-gray-400' : ''}`}
                                            disabled={isLoading}
                                            style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                                        >
                                            <option value="" disabled hidden>Elegí tu género</option>
                                            <option value="female">Mujer</option>
                                            <option value="male">Hombre</option>
                                            <option value="trans">Trans</option>
                                            <option value="other">Otro</option>
                                        </select>
                                    </div>
                                    <div autoComplete="new-password">
                                        <DateOfBirthSelector onDateChange={(date) => { setBirthdate(date); setBirthdateV(false); }} darkMode={darkMode} />
                                    </div>
                                    {BirthdateV && <div className="text-red-500 text-sm ml-1">La fecha de nacimiento es obligatoria</div>}
                                    <div className="pt-2">{continueBtn(done[2])}</div>
                                </div>

                                {/* Slide 3: Ubicación */}
                                <div className="p-6 space-y-4" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
                                    <h1 className={`text-xl font-semibold text-center ${darkMode ? 'text-rose-500' : 'text-rose-500'}`}>Selecciona un país</h1>
                                    <div autoComplete="new-password">
                                        <Paises
                                            selectedCountry={selectedCountry}
                                            handleCountryChangeCallback={handleCountryChange}
                                            darkMode={darkMode}
                                        />
                                    </div>
                                    <div className="pt-2">{continueBtn(done[3])}</div>
                                </div>

                                {/* Slide 4: Foto de perfil */}
                                <div className="p-6 space-y-4" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
                                    <h1 className={`text-xl font-semibold text-center ${darkMode ? 'text-rose-500' : 'text-rose-500'}`}>Foto de perfil</h1>
                                    <p className={`text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        La van a ver los clientes en tu perfil y tus packs.
                                    </p>
                                    <PhotoPicker
                                        file={profilePhoto}
                                        onPick={setProfilePhoto}
                                        isLoading={isLoading}
                                        icon={FaCamera}
                                        label="Subí tu foto de perfil"
                                        darkMode={darkMode}
                                    />
                                    <div className="pt-2">{continueBtn(done[4])}</div>
                                </div>

                                {/* Slide 5: Foto de portada */}
                                <div className="p-6 space-y-4" style={{ width: `${100 / TOTAL_SLIDES}%` }}>
                                    <h1 className={`text-xl font-semibold text-center ${darkMode ? 'text-rose-500' : 'text-rose-500'}`}>Foto de portada</h1>
                                    <p className={`text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Es la imagen grande de fondo en tu perfil.
                                    </p>
                                    <PhotoPicker
                                        file={coverPhoto}
                                        onPick={setCoverPhoto}
                                        isLoading={isLoading}
                                        icon={FaImage}
                                        label="Subí tu foto de portada"
                                        darkMode={darkMode}
                                    />
                                    <div className="pt-2">{continueBtn(done[5], submitAll, 'Finalizar')}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            <Modal ref={modalRef} />
        </LayoutHomeUser>
    );
}

export default ModelCompleteRegistration;
